import { useMemo } from 'react';
import { CURRENCIES, type MarketEvent } from '@/mocks/marketCalendar';
import { toUtcMinutes } from '../calculations';

const BUCKETS = 12; // 2-hour buckets
const BUCKET_LABELS = ['00', '02', '04', '06', '08', '10', '12', '14', '16', '18', '20', '22'];

const IMPACT_WEIGHT = { high: 3, medium: 2, low: 1 } as const;

export default function EventHeatmap({ events }: { events: MarketEvent[] }) {
  const grid = useMemo(() => {
    const map = new Map<string, number[]>(); // currency -> weights per bucket
    for (const c of CURRENCIES) {
      map.set(c.code, new Array(BUCKETS).fill(0));
    }
    for (const e of events) {
      const utc = toUtcMinutes(e.time);
      const bucket = Math.min(BUCKETS - 1, Math.floor(utc / 120));
      const row = map.get(e.currency);
      if (row) row[bucket] += IMPACT_WEIGHT[e.impact];
    }
    return map;
  }, [events]);

  const activeCurrencies = CURRENCIES.filter((c) => (grid.get(c.code) ?? []).some((v) => v > 0));

  const max = useMemo(() => {
    let m = 0;
    for (const row of grid.values()) for (const v of row) m = Math.max(m, v);
    return m || 1;
  }, [grid]);

  const colorFor = (v: number) => {
    if (v === 0) return 'transparent';
    const ratio = v / max;
    if (ratio >= 0.8) return '#6d28d9';
    if (ratio >= 0.6) return '#8b5cf6';
    if (ratio >= 0.4) return '#a78bfa';
    if (ratio >= 0.2) return '#ddd6fe';
    return '#ede9fe';
  };

  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
        Bản đồ nhiệt sự kiện
      </h2>
      <p className="mt-1.5 text-sm text-slate-500">Mức độ tập trung của sự kiện theo đồng tiền và khung giờ.</p>

      <div className="mt-5 overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="flex">
            <div className="w-14 shrink-0" />
            <div className="grid flex-1 grid-cols-12">
              {BUCKET_LABELS.map((l) => (
                <span key={l} className="pb-2 text-center font-mono text-[10px] text-slate-400">
                  {l}
                </span>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            {activeCurrencies.map((c) => {
              const row = grid.get(c.code) ?? [];
              return (
                <div key={c.code} className="flex items-center">
                  <span className="flex w-14 shrink-0 items-center gap-1 pr-2 font-mono text-[11px] font-semibold text-slate-500">
                    <span className="text-sm leading-none">
                      {c.flag}
                    </span>
                    {c.code}
                  </span>
                  <div className="grid flex-1 grid-cols-12 gap-1">
                    {row.map((v, i) => {
                      const color = colorFor(v);
                      const bucketStart = i * 2;
                      return (
                        <div
                          key={i}
                          className="flex h-8 items-center justify-center rounded-md text-[10px] font-semibold"
                          style={{ backgroundColor: color, color: v >= max * 0.6 ? '#fff' : 'transparent' }}
                          title={v > 0 ? `${c.code} · ${String(bucketStart).padStart(2, '0')}:00 · ${v} điểm` : undefined}
                        >
                          {v > 0 && v >= max * 0.4 ? v : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-400">
            <span>Ít</span>
            <span className="h-3 w-3 rounded-sm bg-[#ede9fe]" />
            <span className="h-3 w-3 rounded-sm bg-[#ddd6fe]" />
            <span className="h-3 w-3 rounded-sm bg-[#a78bfa]" />
            <span className="h-3 w-3 rounded-sm bg-[#8b5cf6]" />
            <span className="h-3 w-3 rounded-sm bg-[#6d28d9]" />
            <span>Nhiều</span>
          </div>
        </div>
      </div>
    </section>
  );
}